import { Layout } from "@/components/layout";
import {
  useListEvents,
  useGetDashboardSummary,
  useListEventInventory,
  useListInventory,
  useRefreshEvent,
  useBulkHoldInventory,
  useVerifyHolds,
  useGetSettings,
  useGetMe,
  useGetSalesStatus,
  useSetBuybackGetIn,
  useBulkSetBuybackFveIntent,
  getListEventsQueryKey,
  getGetDashboardSummaryQueryKey,
  getListEventInventoryQueryKey,
  getListInventoryQueryKey,
  type InventoryListing,
} from "@workspace/api-client-react";
import { NeedHelpSection } from "@/components/need-help-section";
import { useQueryClient } from "@tanstack/react-query";
import {
  Loader2,
  RefreshCw,
  Lock,
  Unlock,
  X,
  CalendarDays,
  Users,
  Globe,
  ShieldCheck,
  TrendingUp,
  RotateCcw,
  Tag,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InventoryTable, holdState } from "@/components/inventory-table";
import { getQrCoverage, getLinkCoverage, isBoughtBack } from "@/components/inventory-cells";
import { AccountInventoryTable } from "@/components/account-inventory-table";
import { GlobalSearchResults } from "@/components/global-search-results";
import { ExportCsvButton } from "@/components/export-csv-button";
import { EventsSidebar } from "@/components/events-sidebar";
import { usePersistentState } from "@/hooks/use-persistent-state";
import { useSearch } from "wouter";
import { usePullerFilter, usePullerScope } from "@/contexts/puller-filter";
import { cn } from "@/lib/utils";
import { formatTimeRemaining, formatVenueDateTime } from "@/lib/event-time";
import { showBulkHoldOutcomeToast, showVerifyHoldsOutcomeToast } from "@/lib/hold-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type PhoneFilter = "all" | "good" | "bad" | "unknown";
type BoolFilter = "all" | "yes" | "no";
// QR/ticket-asset coverage filter: "all" clears it, otherwise match the
// derived coverage state from getQrCoverage (full / partial / none).
type QrFilter = "all" | "full" | "partial" | "none";
// FVE lifecycle status (the "Status" column). Selecting "listed" intentionally
// also matches discounted listings (the column shows both as "Listed"), while
// "listed_discount" is a first-class option that matches only discounted rows.
// The filter is multi-select: an empty array means "all statuses".
type StatusValue =
  | "eligible"
  | "held"
  | "listed"
  | "listed_discount"
  | "via_reach"
  | "via_skybox"
  | "cannot_list"
  | "surrender_on_fve"
  | "sold"
  | "sold_partial"
  | "sale_cancelled";

// localStorage keys for persisted inventory filters (global across events).
const FILTER_KEYS = {
  search: "tex-inv-filter-search",
  phone: "tex-inv-filter-phone",
  onHold: "tex-inv-filter-onhold",
  status: "tex-inv-filter-status",
  missingBank: "tex-inv-filter-missing-bank",
  juiceOnly: "tex-inv-filter-juice-only",
  broadcast: "tex-inv-filter-broadcast",
  qr: "tex-inv-filter-qr",
  link: "tex-inv-filter-link",
  boughtBack: "tex-inv-filter-bought-back",
} as const;

// Shared visual style for every control in the filter bar (FilterSelect,
// StatusMultiSelect trigger, ToggleChip) so the bar reads as one cohesive set:
// identical height, border, background, type size and hover state.
const FILTER_CONTROL_CLASS =
  "h-7 px-2 rounded-md border border-border bg-card text-xs text-muted-foreground hover:text-foreground transition-colors";

// Color-coded options for the Status multi-select, ordered to mirror the
// lifecycle. The dot colours match the pill colours used in the Status column
// (see pillMeta in inventory-cells.tsx).
const STATUS_OPTIONS: { value: StatusValue; label: string; dot: string }[] = [
  { value: "eligible", label: "Eligible", dot: "bg-muted-foreground" },
  { value: "held", label: "Held", dot: "bg-blue-500" },
  { value: "listed", label: "Listed", dot: "bg-emerald-500" },
  { value: "listed_discount", label: "Listed at Discount", dot: "bg-amber-500" },
  { value: "via_reach", label: "Selling via Reach", dot: "bg-violet-500" },
  { value: "via_skybox", label: "Via Skybox", dot: "bg-violet-500" },
  { value: "cannot_list", label: "Cannot List", dot: "bg-red-500" },
  { value: "surrender_on_fve", label: "Surrender", dot: "bg-zinc-500" },
  { value: "sold", label: "Sold", dot: "bg-purple-500" },
  { value: "sold_partial", label: "Sold Partial", dot: "bg-orange-500" },
  { value: "sale_cancelled", label: "Sale Cancelled", dot: "bg-amber-500" },
];

export default function Home() {
  const queryClient = useQueryClient();
  const { data: events, isLoading: eventsLoading } = useListEvents();
  const { data: summary } = useGetDashboardSummary();
  const { data: settings } = useGetSettings();
  const defaultHoldDays = settings?.holdDefaults?.holdDays ?? 30;
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);

  const { data: inventory, isLoading: inventoryLoading } = useListEventInventory(
    selectedEventId || 0,
    {
      query: {
        enabled: !!selectedEventId,
        queryKey: getListEventInventoryQueryKey(selectedEventId || 0),
      },
    },
  );

  // ----- Filters -----
  // Persisted globally (across reloads and event switches) so operators return
  // to their working view instantly. "Reset filters" clears them all.
  const [search, setSearch] = usePersistentState(FILTER_KEYS.search, "");
  // Deep-link from the Emails drawer: `/?focus=<reachListingId>` seeds the
  // global search so the linked listing surfaces immediately.
  const routeSearch = useSearch();
  useEffect(() => {
    const focus = new URLSearchParams(routeSearch).get("focus");
    if (focus) setSearch(focus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeSearch]);
  const [phone, setPhone] = usePersistentState<PhoneFilter>(FILTER_KEYS.phone, "all");
  const [onHold, setOnHold] = usePersistentState<BoolFilter>(FILTER_KEYS.onHold, "all");
  const [statusRaw, setStatuses] = usePersistentState<StatusValue[]>(FILTER_KEYS.status, []);
  // Stale single-string values from the old single-select filter (e.g. "all")
  // are gracefully discarded: a non-array means "show all".
  const statuses = Array.isArray(statusRaw) ? statusRaw : [];
  const [missingBank, setMissingBank] = usePersistentState(FILTER_KEYS.missingBank, false);
  const [juiceOnly, setJuiceOnly] = usePersistentState(FILTER_KEYS.juiceOnly, false);
  // Reach-specific filter. broadcast keys off the serialized reachBroadcast
  // tri-state (null = unknown, excluded when a definitive value is required).
  const [broadcast, setBroadcast] = usePersistentState<BoolFilter>(FILTER_KEYS.broadcast, "all");
  // QR coverage filter (full / partial / none); "all" = no filtering.
  const [qrFilter, setQrFilter] = usePersistentState<QrFilter>(FILTER_KEYS.qr, "all");
  // Transfer-link: linked rows are hidden by default (mirrors "Show bought-back").
  // A stale string value from the old dropdown (e.g. "all", "none") is coerced
  // to false so upgrades don't break the new boolean toggle.
  const [showLinkedRaw, setShowLinked] = usePersistentState<unknown>(FILTER_KEYS.link, false);
  const showLinked = showLinkedRaw === true;
  // Bought-back listings (green DETECTED — a confirmed Reach buyback PO) are
  // hidden by default so the working view isn't cluttered with completed
  // buy-backs. The "Show bought-back" chip surfaces them again on demand.
  const [showBoughtBack, setShowBoughtBack] = usePersistentState(
    FILTER_KEYS.boughtBack,
    false,
  );

  // The puller filter is now global (header dropdown) and applies across the
  // whole app, so it is no longer part of the per-event filter bar.
  const { pullerId, pullerName, showBadPhones } = usePullerFilter();
  const { isAll, ready, eventIdsWithInventory, scopedInventory } = usePullerScope();

  // By Event vs By Account workspace view. Only meaningful for a single puller
  // (account mode groups across all of that puller's events); when "All Pullers"
  // is active we always fall back to By Event.
  const [viewMode, setViewMode] = usePersistentState<"event" | "account">(
    "tex-workspace-view",
    "event",
  );
  const accountMode = !isAll && viewMode === "account";

  // ----- Global search -----
  // A single token that looks like a listing ID (e.g. "AB12345" or a long
  // all-numeric Reach id like "677674555") or any term containing "@" (an
  // account email) switches the workspace into a cross-event lookup. Short
  // numeric section/row/seat searches stay scoped to the current view, so
  // everyday filtering is unaffected: a bare 3-char token only goes global when
  // it contains a letter, while purely numeric ids must be 6+ digits to qualify.
  const searchTerm = search.trim();
  const isGlobalSearch =
    searchTerm.length > 0 &&
    !/\s/.test(searchTerm) &&
    (searchTerm.includes("@") ||
      (/^[a-z0-9-]{3,}$/i.test(searchTerm) &&
        (/[a-z]/i.test(searchTerm) || searchTerm.length >= 6)));

  const GLOBAL_RESULT_CAP = 200;
  const { data: globalResultsRaw, isLoading: globalLoading } = useListInventory(
    { search: searchTerm },
    {
      query: {
        enabled: isGlobalSearch,
        queryKey: getListInventoryQueryKey({ search: searchTerm }),
      },
    },
  );
  const globalResults = useMemo(
    () => (globalResultsRaw ?? []).slice(0, GLOBAL_RESULT_CAP),
    [globalResultsRaw],
  );
  const globalTotal = globalResultsRaw?.length ?? 0;
  const globalEventCount = useMemo(
    () => new Set((globalResultsRaw ?? []).map((r) => r.eventId ?? r.eventName)).size,
    [globalResultsRaw],
  );

  const filtersActive =
    search.trim() !== "" ||
    phone !== "all" ||
    onHold !== "all" ||
    statuses.length > 0 ||
    missingBank ||
    juiceOnly ||
    broadcast !== "all" ||
    qrFilter !== "all" ||
    showLinked ||
    showBoughtBack;

  const resetFilters = () => {
    setSearch("");
    setPhone("all");
    setOnHold("all");
    setStatuses([]);
    setMissingBank(false);
    setJuiceOnly(false);
    setBroadcast("all");
    setQrFilter("all");
    setShowLinked(false);
    setShowBoughtBack(false);
  };

  // Shared row predicate for both views. The global "show bad phones" toggle
  // hides bad-phone listings everywhere by default, unless the operator has
  // explicitly filtered to phone === "bad" (in which case showing them is the
  // obvious intent).
  // Everything in rowPasses except the status filter, so status counts can be
  // computed against inventory filtered by all the OTHER active filters.
  const rowPassesExceptStatus = useCallback(
    (r: InventoryListing) => {
      const q = search.trim().toLowerCase();
      if (q) {
        const hay = [r.section, r.row, r.seatFrom, r.seatTo, r.vendorAccountEmail, r.pullerName]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      const phoneStatus = r.phoneStatus ?? "unknown";
      if (!showBadPhones && phone !== "bad" && phoneStatus === "bad") return false;
      if (phone !== "all" && phoneStatus !== phone) return false;
      if (onHold === "yes" && !r.adminHoldActive) return false;
      if (onHold === "no" && r.adminHoldActive) return false;
      if (missingBank && r.bankMappingStatus) return false;
      if (juiceOnly && !r.historicalJuiceRenterName) return false;
      // Broadcasted: null reachBroadcast (unknown) is excluded from both Yes and
      // No so the filter only ever surfaces rows with a definitive value.
      if (broadcast === "yes" && r.reachBroadcast !== true) return false;
      if (broadcast === "no" && r.reachBroadcast !== false) return false;
      // QR coverage: match the derived ticket-asset coverage state.
      if (qrFilter !== "all" && getQrCoverage(r) !== qrFilter) return false;
      // Transfer-link: hide rows that already have a link unless the operator
      // has toggled "Show linked" on.
      if (!showLinked && (getLinkCoverage(r) === "full" || getLinkCoverage(r) === "partial")) return false;
      // Hide bought-back listings (confirmed Reach PO / green DETECTED) unless
      // the operator has explicitly toggled them back into view.
      if (!showBoughtBack && isBoughtBack(r)) return false;
      return true;
    },
    [search, phone, onHold, missingBank, juiceOnly, broadcast, qrFilter, showLinked, showBoughtBack, showBadPhones],
  );

  const rowPasses = useCallback(
    (r: InventoryListing) => {
      if (!rowPassesExceptStatus(r)) return false;
      if (statuses.length > 0) {
        const s = r.fveStatus ?? "eligible";
        // Selecting "listed" matches both plain and discounted listings (the
        // pill renders "Listed" for both); "listed_discount" matches only
        // discounted rows. Any other selected value is an exact match.
        const matches = statuses.some((sel) =>
          sel === "listed" ? s === "listed" || s === "listed_discount" : s === sel,
        );
        if (!matches) return false;
      }
      return true;
    },
    [rowPassesExceptStatus, statuses],
  );

  // By Event: the open event's inventory, also scoped to the active puller.
  const filtered = useMemo(
    () =>
      (inventory || []).filter(
        (r) => rowPasses(r) && (!pullerName || r.pullerName === pullerName),
      ),
    [inventory, rowPasses, pullerName],
  );

  // By Account: the puller's full cross-event inventory (already puller-scoped).
  const accountData = useMemo(
    () => (scopedInventory || []).filter(rowPasses),
    [scopedInventory, rowPasses],
  );

  // Per-status counts for the Status dropdown pills. Computed against the
  // current view's inventory filtered by every OTHER active filter (phone,
  // hold, search, missing bank, juice) but ignoring the status filter itself.
  const statusCounts = useMemo(() => {
    const counts: Partial<Record<StatusValue, number>> = {};
    const src = accountMode
      ? scopedInventory || []
      : (inventory || []).filter((r) => !pullerName || r.pullerName === pullerName);
    for (const r of src) {
      if (!rowPassesExceptStatus(r)) continue;
      const s = (r.fveStatus ?? "eligible") as StatusValue;
      counts[s] = (counts[s] ?? 0) + (r.availableQuantity ?? 1);
    }
    return counts;
  }, [accountMode, scopedInventory, inventory, pullerName, rowPassesExceptStatus]);

  // The rows the current view is showing — drives selection helpers below.
  const visibleRows = accountMode ? accountData : filtered;

  // The exact filtered rows + a contextual file name for the CSV export, keyed
  // to whichever view is active (Global search / By Account / By Event).
  const exportContext = useMemo(() => {
    if (isGlobalSearch) {
      return { rows: globalResults, base: `search-${searchTerm}` };
    }
    if (accountMode) {
      return {
        rows: accountData,
        base: pullerName ? `${pullerName}-account-export` : "account-export",
      };
    }
    const ev = events?.find((e) => e.id === selectedEventId);
    return { rows: filtered, base: ev?.label ?? "event-export" };
  }, [
    isGlobalSearch,
    accountMode,
    globalResults,
    accountData,
    filtered,
    searchTerm,
    pullerName,
    events,
    selectedEventId,
  ]);

  // When a specific puller is selected, the sidebar only lists events that have
  // remaining inventory for that puller. While the scoped inventory query is
  // still in flight (`!ready`), keep the full event list visible so events
  // don't vanish for the duration of the round-trip.
  const visibleEvents = useMemo(() => {
    if (isAll || !ready) return events;
    return (events ?? []).filter((e) => eventIdsWithInventory.has(e.id));
  }, [events, isAll, ready, eventIdsWithInventory]);

  // True once a puller is selected and its inventory has loaded but no
  // configured event has any remaining inventory for it (e.g. a tagless puller
  // like "Reach" the API can't scope). Used to show a clearer empty state.
  const pullerHasNoInventory = useMemo(
    () =>
      !isAll &&
      ready &&
      (events?.length ?? 0) > 0 &&
      (visibleEvents?.length ?? 0) === 0,
    [isAll, ready, events, visibleEvents],
  );

  // Deselect the open event if the active puller filter no longer includes it.
  useEffect(() => {
    if (isAll || !ready) return;
    if (selectedEventId != null && !eventIdsWithInventory.has(selectedEventId)) {
      setSelectedEventId(null);
    }
  }, [isAll, ready, selectedEventId, eventIdsWithInventory]);

  // On first load only, auto-select the earliest upcoming event from the
  // visible (puller-filtered) set so operators land straight on data. "Upcoming"
  // uses the same threshold as the sidebar: an event is past only when it has a
  // date strictly before today (no-date events count as upcoming). We pick the
  // soonest upcoming event; if every event is past, we fall back to the most
  // recent past one so operators still land on data. The one-shot ref means
  // this fires exactly once — after the initial event load — so a later manual
  // (or puller-driven) deselect is never overridden by a re-selection. The
  // "YYYY-MM-DD HH:mm:ss" format sorts correctly as a string.
  const hasAutoSelectedRef = useRef(false);
  useEffect(() => {
    if (hasAutoSelectedRef.current) return;
    if (eventsLoading) return;
    if (!isAll && !ready) return;
    if (selectedEventId != null) {
      hasAutoSelectedRef.current = true;
      return;
    }
    const candidates = visibleEvents ?? [];
    if (candidates.length === 0) return;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const upcoming: typeof candidates = [];
    const past: typeof candidates = [];
    for (const e of candidates) {
      const d = parseEventDate(e.eventDate);
      if (d && d.getTime() < today.getTime()) past.push(e);
      else upcoming.push(e);
    }
    let chosen;
    if (upcoming.length > 0) {
      // Earliest upcoming. Treat a missing date as "latest" (\uffff sorts after
      // any real date string) so a dated upcoming event is always preferred.
      chosen = upcoming.reduce((best, e) =>
        (e.eventDate ?? "\uffff") < (best.eventDate ?? "\uffff") ? e : best,
      );
    } else {
      // All past: fall back to the most recent past event.
      chosen = past.reduce((best, e) =>
        (e.eventDate ?? "") > (best.eventDate ?? "") ? e : best,
      );
    }
    hasAutoSelectedRef.current = true;
    setSelectedEventId(chosen.id);
  }, [eventsLoading, isAll, ready, selectedEventId, visibleEvents]);

  // ----- Selection -----
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const selectIds = (ids: number[], selected: boolean) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (selected ? next.add(id) : next.delete(id)));
      return next;
    });
  const toggleSelectAll = () =>
    setSelectedIds((prev) => {
      const visibleIds = visibleRows.map((r) => r.id);
      const allOn = visibleIds.every((id) => prev.has(id)) && visibleIds.length > 0;
      return allOn ? new Set() : new Set(visibleIds);
    });
  const allSelected =
    visibleRows.length > 0 && visibleRows.every((r) => selectedIds.has(r.id));

  const clearSelection = () => setSelectedIds(new Set());

  // Preserve the last non-zero count so the floating bar shows a number while
  // it animates out after the selection clears.
  const [selectionCount, setSelectionCount] = useState(0);
  useEffect(() => {
    if (selectedIds.size > 0) setSelectionCount(selectedIds.size);
  }, [selectedIds]);

  // Changing the global puller filter (puller or bad-phones toggle) or any
  // inventory filter/search can drop previously-selected rows from view, so
  // clear the selection — mirroring the Accounts page — to ensure a bulk
  // hold/release can only ever act on rows the operator can currently see.
  useEffect(() => {
    clearSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pullerId, showBadPhones, search, phone, onHold, statuses, missingBank, juiceOnly, broadcast, qrFilter, showLinked]);

  // ----- Mutations -----
  const invalidate = () => {
    if (selectedEventId)
      queryClient.invalidateQueries({ queryKey: getListEventInventoryQueryKey(selectedEventId) });
    // Cross-event list backing the By Account view (prefix-matches the
    // pullerId-scoped variant) — bulk actions in account mode span events.
    queryClient.invalidateQueries({ queryKey: getListInventoryQueryKey() });
    queryClient.invalidateQueries({ queryKey: getGetDashboardSummaryQueryKey() });
    queryClient.invalidateQueries({ queryKey: getListEventsQueryKey() });
  };

  const refresh = useRefreshEvent({ mutation: { onSuccess: invalidate } });
  const verifyHolds = useVerifyHolds();

  // "Apply get-in to all" — bulk sets the resale get-in price for every
  // listing in the current buyback-enabled event. Each row is updated
  // individually (the API has no bulk endpoint for this); the button shows a
  // progress count while mutations fire.
  const [bulkGetInOpen, setBulkGetInOpen] = useState(false);
  const [bulkGetInValue, setBulkGetInValue] = useState("");
  const [bulkGetInProgress, setBulkGetInProgress] = useState<null | {
    done: number;
    total: number;
  }>(null);
  const setGetIn = useSetBuybackGetIn();
  const bulkSetFveIntent = useBulkSetBuybackFveIntent();
  const selectedEvent = events?.find((e) => e.id === selectedEventId);
  const isBuybackEvent = selectedEvent?.buybacksEnabled ?? false;
  const hideGetIn = settings?.hideGetIn ?? false;

  // Independent grouping toggles. Both are persisted globally in localStorage
  // so operators return to their last-used combination across events.
  // "Group by Puller" is available for all events; "Group by Purchase" is shown
  // (and meaningful) only on buyback events where listings have purchase IDs.
  const [groupByPuller, setGroupByPuller] = usePersistentState("tex-group-by-puller", true);
  const [groupByPurchase, setGroupByPurchase] = usePersistentState(
    "tex-group-by-purchase",
    true,
  );

  // Derive the active grouping mode from the two booleans. Non-buyback events
  // ignore the purchase dimension entirely (purchase IDs aren't meaningful there).
  const activeGroupBy: "puller" | "purchase" | "both" | "flat" = useMemo(() => {
    if (!isBuybackEvent) return groupByPuller ? "puller" : "flat";
    if (groupByPuller && groupByPurchase) return "both";
    if (groupByPuller) return "puller";
    if (groupByPurchase) return "purchase";
    return "flat";
  }, [isBuybackEvent, groupByPuller, groupByPurchase]);

  const handleApplyGetInToAll = async () => {
    const num = Number(bulkGetInValue);
    if (!Number.isFinite(num) || num < 0 || !inventory) return;
    const rows = inventory.filter((r) => r.buybacksEnabled);
    setBulkGetInProgress({ done: 0, total: rows.length });
    let done = 0;
    for (const r of rows) {
      try {
        await setGetIn.mutateAsync({ id: r.id, data: { getIn: num } });
      } catch {
        // Best-effort — keep going for remaining rows.
      }
      done++;
      setBulkGetInProgress({ done, total: rows.length });
    }
    invalidate();
    setBulkGetInProgress(null);
    setBulkGetInOpen(false);
    setBulkGetInValue("");
  };

  // On-demand hold re-check. Only operators can place/release holds, so only
  // they get the manual re-verify action; it's hidden when Reach isn't wired up
  // (the endpoint would just report every row as "skipped").
  const { data: me } = useGetMe();
  const isOperator = me?.role === "admin" || me?.role === "operator";
  const { data: salesStatus } = useGetSalesStatus();
  const reachConfigured = salesStatus?.reachConfigured ?? false;

  // Re-verify the holds the operator is currently looking at, not the whole
  // account — matches the "currently shown" scope of every other bulk action.
  const heldIds = useMemo(
    () => visibleRows.filter((r) => r.adminHoldActive).map((r) => r.id),
    [visibleRows],
  );
  const refreshHolds = useVerifyHolds({
    mutation: {
      onSuccess: (res) => {
        invalidate();
        showVerifyHoldsOutcomeToast(res);
      },
    },
  });
  const bulkHold = useBulkHoldInventory({
    mutation: {
      onSuccess: async (res, variables) => {
        // Read-back verification of the holds we just placed/released. The
        // verify result reflects Reach's actual state, so prefer it for the
        // operator-facing toast; fall back to the bulk response if it fails.
        const ids = res.results.map((r) => r.inventoryId).filter((v): v is number => v != null);
        let summary = res;
        if (ids.length) {
          try {
            summary = await verifyHolds.mutateAsync({ data: { inventoryIds: ids } });
          } catch {
            /* verification is best-effort */
          }
        }
        invalidate();
        clearSelection();
        showBulkHoldOutcomeToast(summary, variables.data.action);
      },
    },
  });

  // The confirm dialog carries the exact listing ids it will act on. The
  // floating bulk bar passes the current selection; the By Account headers pass
  // every listing in that account (across events). Both share this one dialog
  // and the same bulk mutation + read-back verification.
  const [confirm, setConfirm] = useState<
    null | { action: "hold" | "unhold"; ids: number[] }
  >(null);

  const runBulk = () => {
    if (!confirm) return;
    bulkHold.mutate({
      data: {
        inventoryIds: confirm.ids,
        action: confirm.action,
        ...(confirm.action === "hold" ? { holdDays: defaultHoldDays } : {}),
      },
    });
    setConfirm(null);
  };

  const confirmCount = confirm?.ids.length ?? 0;
  const heldInConfirm = useMemo(() => {
    if (!confirm) return 0;
    const ids = new Set(confirm.ids);
    return visibleRows.filter((r) => ids.has(r.id) && r.adminHoldActive).length;
  }, [confirm, visibleRows]);

  return (
    <Layout>
      <div className="flex h-full w-full bg-background overflow-hidden">
        {/* Left Sidebar: Events List */}
        <EventsSidebar
          events={visibleEvents}
          eventsLoading={eventsLoading}
          selectedEventId={accountMode ? null : selectedEventId}
          onSelectEvent={(id) => {
            setViewMode("event");
            setSelectedEventId(id);
            clearSelection();
          }}
          summary={summary}
          pullerHasNoInventory={pullerHasNoInventory}
        />

        {/* Center/Right: Inventory Workspace */}
        <div className="flex-1 flex flex-col h-full bg-background min-w-0">
          {accountMode || selectedEventId || !isAll || isGlobalSearch ? (
            <div className="flex-1 flex flex-col h-full p-4 overflow-hidden">
              <div className="mb-3 flex items-center justify-between shrink-0 gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="min-w-0">
                    <h1 className="text-lg font-medium text-foreground truncate">
                      {isGlobalSearch
                        ? "Global search"
                        : accountMode
                          ? `${pullerName ?? "Puller"} · By account`
                          : selectedEventId
                            ? events?.find((e) => e.id === selectedEventId)?.label
                            : (pullerName ?? "Workspace")}
                    </h1>
                    {!accountMode &&
                      !isGlobalSearch &&
                      selectedEventId &&
                      (() => {
                        // Venue-local start time + a time-remaining countdown for
                        // the selected event, computed once at render (not a live
                        // ticker). The timezone abbreviation in the datetime makes
                        // it clear the time is the venue's own local time.
                        const ev = events?.find((e) => e.id === selectedEventId);
                        const dateTime = ev
                          ? formatVenueDateTime(ev.eventDate, ev.eventTimezone)
                          : null;
                        const remaining = ev
                          ? formatTimeRemaining(ev.eventDate, ev.eventTimezone)
                          : null;
                        const line = [dateTime, remaining]
                          .filter(Boolean)
                          .join(" · ");
                        return line ? (
                          <p className="-mt-0.5 text-xs text-muted-foreground truncate">
                            {line}
                          </p>
                        ) : null;
                      })()}
                  </div>
                  {!isAll && !isGlobalSearch && (
                    <ViewToggle
                      value={viewMode}
                      onChange={(mode) => {
                        setViewMode(mode);
                        clearSelection();
                      }}
                    />
                  )}
                  {!accountMode && !isGlobalSearch && selectedEventId && (
                    <GroupByToggles
                      groupByPuller={groupByPuller}
                      groupByPurchase={groupByPurchase}
                      isBuybackEvent={isBuybackEvent}
                      onTogglePuller={() => setGroupByPuller((v) => !v)}
                      onTogglePurchase={() => setGroupByPurchase((v) => !v)}
                    />
                  )}
                </div>
                {(accountMode || selectedEventId || isGlobalSearch) && (
                  <div className="flex gap-2 shrink-0">
                    {!isGlobalSearch && !accountMode && selectedEventId && (
                      <>
                        <Button
                          variant="outline" size="sm" className="h-7 text-xs px-2.5 bg-card"
                          disabled={refresh.isPending}
                          onClick={() => refresh.mutate({ id: selectedEventId, data: { mode: "reach" } })}
                        >
                          {refresh.isPending && refresh.variables?.data?.mode === "reach" ? (
                            <Loader2 className="h-3 w-3 animate-spin mr-1" />
                          ) : (
                            <RefreshCw className="h-3 w-3 mr-1" />
                          )}
                          Reach
                        </Button>
                        <Button
                          variant="outline" size="sm" className="h-7 text-xs px-2.5 bg-card"
                          disabled={refresh.isPending}
                          onClick={() => refresh.mutate({ id: selectedEventId, data: { mode: "amp" } })}
                        >
                          {refresh.isPending && refresh.variables?.data?.mode === "amp" ? (
                            <Loader2 className="h-3 w-3 animate-spin mr-1" />
                          ) : (
                            <RefreshCw className="h-3 w-3 mr-1" />
                          )}
                          AMP
                        </Button>
                        {/* Apply get-in to all — only for buyback events without hideGetIn */}
                        {isBuybackEvent && isOperator && !hideGetIn && (
                          <Popover open={bulkGetInOpen} onOpenChange={setBulkGetInOpen}>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 text-xs px-2.5 bg-card rounded-full"
                                disabled={!!bulkGetInProgress}
                              >
                                {bulkGetInProgress ? (
                                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                ) : (
                                  <TrendingUp className="h-3 w-3 mr-1 text-emerald-400" />
                                )}
                                {bulkGetInProgress
                                  ? `${bulkGetInProgress.done}/${bulkGetInProgress.total}`
                                  : "Set get-in"}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-56 p-3" align="end">
                              <p className="text-xs font-semibold mb-2">Apply get-in to all listings</p>
                              <p className="text-[11px] text-muted-foreground mb-3">
                                Sets the resale price for every listing in this event. Each row's margin will be recomputed.
                              </p>
                              <div className="flex gap-2">
                                <Input
                                  className="h-7 text-xs font-mono flex-1"
                                  placeholder="e.g. 95.00"
                                  value={bulkGetInValue}
                                  onChange={(e) => setBulkGetInValue(e.target.value)}
                                  onKeyDown={(e) => e.key === "Enter" && handleApplyGetInToAll()}
                                  autoFocus
                                />
                                <Button
                                  size="sm"
                                  className="h-7 text-xs px-2.5"
                                  disabled={!bulkGetInValue || !!bulkGetInProgress}
                                  onClick={handleApplyGetInToAll}
                                >
                                  Apply
                                </Button>
                              </div>
                            </PopoverContent>
                          </Popover>
                        )}
                      </>
                    )}
                    {!isGlobalSearch && isOperator && reachConfigured && (
                      <Button
                        variant="outline" size="sm" className="h-7 text-xs px-2.5 bg-card rounded-full"
                        disabled={refreshHolds.isPending || heldIds.length === 0}
                        title={
                          heldIds.length === 0
                            ? "No held listings in view to re-check"
                            : `Re-check ${heldIds.length} held listing${heldIds.length === 1 ? "" : "s"} against Reach`
                        }
                        onClick={() => refreshHolds.mutate({ data: { inventoryIds: heldIds } })}
                      >
                        {refreshHolds.isPending ? (
                          <Loader2 className="h-3 w-3 animate-spin mr-1" />
                        ) : (
                          <ShieldCheck className="h-3 w-3 mr-1 text-blue-400" />
                        )}
                        Refresh holds
                      </Button>
                    )}
                    <ExportCsvButton
                      listings={exportContext.rows}
                      filenameBase={exportContext.base}
                    />
                  </div>
                )}
              </div>

              {accountMode || selectedEventId || isGlobalSearch ? (
              <div className="flex-1 border border-border rounded-md bg-card overflow-hidden flex flex-col relative">
                {/* Filter bar */}
                <div className="p-2 border-b border-border bg-panel flex flex-wrap gap-2 items-center shrink-0">
                  <Input
                    placeholder="Search section / row / seat / account / puller — or a listing ID / email for all events"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="h-7 text-xs w-72"
                  />
                  <FilterSelect label="Phone" value={phone} onChange={(v) => setPhone(v as PhoneFilter)}
                    options={[["all", "All phones"], ["good", "Good"], ["bad", "Bad"], ["unknown", "Unknown"]]} />
                  <StatusMultiSelect selected={statuses} onChange={setStatuses} counts={statusCounts} />
                  <ToggleChip active={missingBank} onClick={() => setMissingBank((v) => !v)}>Missing Bank</ToggleChip>
                  <ToggleChip active={juiceOnly} onClick={() => setJuiceOnly((v) => !v)}>Juice renter</ToggleChip>
                  <ToggleChip active={showBoughtBack} onClick={() => setShowBoughtBack((v) => !v)}>Show bought-back</ToggleChip>
                  <ToggleChip active={showLinked} onClick={() => setShowLinked(!showLinked)}>Show linked</ToggleChip>
                  {/* Reach group: Broadcasted / Has Assets / Hold, kept together
                      so the Reach-specific filters read as one unit. */}
                  <div className="inline-flex items-center gap-1 rounded-md border border-border bg-panel pl-2 pr-1">
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Reach
                    </span>
                    <FilterSelect label="Broadcasted" value={broadcast} onChange={(v) => setBroadcast(v as BoolFilter)}
                      options={[["all", "Broadcast"], ["yes", "Broadcasted"], ["no", "Not broadcast"]]} />
                    <FilterSelect label="Hold" value={onHold} onChange={(v) => setOnHold(v as BoolFilter)}
                      options={[["all", "Hold"], ["yes", "On hold"], ["no", "Not held"]]} />
                  </div>
                  <FilterSelect label="QR" value={qrFilter} onChange={(v) => setQrFilter(v as QrFilter)}
                    options={[["all", "QR"], ["full", "Has QR"], ["partial", "Partial"], ["none", "No QR"]]} />
                  {filtersActive && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs px-2 text-muted-foreground hover:text-foreground"
                      onClick={resetFilters}
                    >
                      <X className="h-3 w-3 mr-1" /> Reset filters
                    </Button>
                  )}
                  <span className="ml-auto text-[11px] font-mono text-muted-foreground">
                    {isGlobalSearch
                      ? `${globalResults.length}${globalTotal > globalResults.length ? `/${globalTotal}` : ""}`
                      : accountMode
                        ? `${accountData.length}/${scopedInventory?.length ?? 0}`
                        : `${filtered.length}/${inventory?.length ?? 0}`}
                  </span>
                </div>

                {/* Global search banner */}
                {isGlobalSearch && (
                  <div className="px-3 py-2 border-b border-border bg-primary/5 flex items-center gap-2 text-xs shrink-0">
                    <Globe className="h-3.5 w-3.5 text-primary shrink-0" />
                    <span className="text-foreground">
                      Global results for{" "}
                      <span className="font-mono font-medium">'{searchTerm}'</span>
                      {!globalLoading && (
                        <span className="text-muted-foreground">
                          {" "}— {globalTotal} row{globalTotal === 1 ? "" : "s"} across{" "}
                          {globalEventCount} event{globalEventCount === 1 ? "" : "s"}
                          {globalTotal > globalResults.length
                            ? ` (showing first ${globalResults.length})`
                            : ""}
                        </span>
                      )}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 text-xs px-2 ml-auto text-muted-foreground hover:text-foreground"
                      onClick={() => setSearch("")}
                    >
                      <X className="h-3 w-3 mr-1" /> Clear search
                    </Button>
                  </div>
                )}

                <div className="flex-1 overflow-hidden">
                  {isGlobalSearch ? (
                    globalLoading ? (
                      <div className="flex justify-center p-10"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
                    ) : (
                      <GlobalSearchResults
                        data={globalResults}
                        defaultHoldDays={defaultHoldDays}
                      />
                    )
                  ) : accountMode ? (
                    !ready ? (
                      <div className="flex justify-center p-10"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
                    ) : (
                      <AccountInventoryTable
                        data={accountData}
                        selectedIds={selectedIds}
                        onToggleSelectAll={toggleSelectAll}
                        onSelectIds={selectIds}
                        allSelected={allSelected}
                        defaultHoldDays={defaultHoldDays}
                        onBulkAccount={(ids, action) => setConfirm({ action, ids })}
                        bulkPending={bulkHold.isPending}
                      />
                    )
                  ) : inventoryLoading ? (
                    <div className="flex justify-center p-10"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
                  ) : (
                    <>
                      {/* Dedicated Need Help section — unclaimed broadcast rows from other
                          pullers are pulled OUT of the main table and shown here with inline
                          Offer Assistance actions. Claimed rows remain in the main table. */}
                      {(() => {
                        const myTag = me?.pullerTag;
                        if (!myTag) return null;
                        const needHelpRows = filtered.filter(
                          (r) => r.needHelp && r.pullerTag !== myTag && r.needHelpPullerId == null,
                        );
                        if (needHelpRows.length === 0) return null;
                        return (
                          <NeedHelpSection
                            rows={needHelpRows}
                            onClaimed={invalidate}
                          />
                        );
                      })()}
                      <InventoryTable
                        data={filtered.filter((r) => {
                          const myTag = me?.pullerTag;
                          if (!myTag) return true;
                          // Exclude unclaimed broadcast rows — they're in the Need Help section
                          return !(r.needHelp && r.pullerTag !== myTag && r.needHelpPullerId == null);
                        })}
                        eventId={selectedEventId!}
                        selectedIds={selectedIds}
                        onToggleSelectAll={toggleSelectAll}
                        onSelectIds={selectIds}
                        allSelected={allSelected}
                        defaultHoldDays={defaultHoldDays}
                        hideGetIn={hideGetIn}
                        groupBy={activeGroupBy}
                      />
                    </>
                  )}
                </div>

                {/* Floating bulk action bar (selection is disabled in global search) */}
                <div
                  className={cn(
                    "absolute bottom-4 left-1/2 -translate-x-1/2 z-20 transition-all duration-200 ease-out",
                    selectedIds.size > 0 && !isGlobalSearch
                      ? "translate-y-0 opacity-100"
                      : "translate-y-4 opacity-0 pointer-events-none",
                  )}
                  aria-hidden={selectedIds.size === 0 || isGlobalSearch}
                >
                  <div className="flex items-center gap-3 rounded-full border border-border bg-card/95 backdrop-blur px-4 py-2 shadow-lg shadow-black/20">
                    <span className="text-xs font-medium text-foreground whitespace-nowrap">
                      {selectionCount} selected
                    </span>
                    <div className="h-4 w-px bg-border" />
                    <Button size="sm" className="h-7 text-xs px-2.5"
                      onClick={() => setConfirm({ action: "hold", ids: [...selectedIds] })} disabled={bulkHold.isPending}>
                      <Lock className="h-3 w-3 mr-1" /> Place holds
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs px-2.5 bg-card"
                      onClick={() => setConfirm({ action: "unhold", ids: [...selectedIds] })} disabled={bulkHold.isPending}>
                      <Unlock className="h-3 w-3 mr-1" /> Release holds
                    </Button>
                    {/* Bulk FVE intent — buyback events in buyback-display mode only.
                        Stamps Buy Back / Sell on every selected listing at once. */}
                    {isBuybackEvent && isOperator && hideGetIn && selectedEventId != null && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button size="sm" variant="outline" className="h-7 text-xs px-2.5 bg-card"
                            disabled={bulkSetFveIntent.isPending}>
                            {bulkSetFveIntent.isPending ? (
                              <Loader2 className="h-3 w-3 animate-spin mr-1" />
                            ) : (
                              <Tag className="h-3 w-3 mr-1 text-emerald-400" />
                            )}
                            Set intent
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-48 p-1" align="center">
                          <p className="text-[10px] text-muted-foreground px-2 py-1 font-medium uppercase tracking-wide">
                            Set intent for {selectionCount}
                          </p>
                          <button
                            type="button"
                            className="w-full text-left px-2 py-1.5 text-xs rounded hover:bg-accent transition-colors flex items-center gap-2"
                            onClick={() =>
                              bulkSetFveIntent.mutate(
                                { id: selectedEventId, data: { intent: "buy_back", ids: [...selectedIds] } },
                                { onSuccess: () => { invalidate(); clearSelection(); } },
                              )
                            }
                          >
                            <RotateCcw className="h-3 w-3 text-amber-400" /> Buy Back
                          </button>
                          <button
                            type="button"
                            className="w-full text-left px-2 py-1.5 text-xs rounded hover:bg-accent transition-colors flex items-center gap-2"
                            onClick={() =>
                              bulkSetFveIntent.mutate(
                                { id: selectedEventId, data: { intent: "sell", ids: [...selectedIds] } },
                                { onSuccess: () => { invalidate(); clearSelection(); } },
                              )
                            }
                          >
                            <Tag className="h-3 w-3 text-emerald-400" /> Sell
                          </button>
                          <div className="border-t border-border my-1" />
                          <button
                            type="button"
                            className="w-full text-left px-2 py-1.5 text-xs rounded hover:bg-accent transition-colors text-muted-foreground"
                            onClick={() =>
                              bulkSetFveIntent.mutate(
                                { id: selectedEventId, data: { intent: null, ids: [...selectedIds] } },
                                { onSuccess: () => { invalidate(); clearSelection(); } },
                              )
                            }
                          >
                            Clear intent
                          </button>
                        </PopoverContent>
                      </Popover>
                    )}
                    {bulkHold.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />}
                    <Button size="sm" variant="ghost" className="h-7 text-xs px-2" onClick={clearSelection}>
                      <X className="h-3 w-3 mr-1" /> Clear
                    </Button>
                  </div>
                </div>
              </div>
              ) : (
                <div className="flex-1 border border-border rounded-md bg-card flex items-center justify-center text-sm text-muted-foreground">
                  Select an event to view inventory, or switch to By Account
                </div>
              )}
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
              Select an event to view inventory
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.action === "hold" ? "Place admin holds" : "Release admin holds"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.action === "hold" ? (
                <>This will place a {defaultHoldDays}-day Reach admin hold on <strong>{confirmCount}</strong> listing(s). Each hold is verified by reading it back from Reach.</>
              ) : (
                <>This will release Reach admin holds on <strong>{confirmCount}</strong> listing(s) ({heldInConfirm} currently held).</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={runBulk}>
              {confirm?.action === "hold" ? "Place holds" : "Release holds"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}

function ViewToggle({
  value,
  onChange,
}: {
  value: "event" | "account";
  onChange: (mode: "event" | "account") => void;
}) {
  const options: { mode: "event" | "account"; label: string; Icon: typeof CalendarDays }[] = [
    { mode: "event", label: "By Event", Icon: CalendarDays },
    { mode: "account", label: "By Account", Icon: Users },
  ];
  return (
    <div className="inline-flex items-center rounded-md border border-border bg-panel p-0.5">
      {options.map(({ mode, label, Icon }) => (
        <button
          key={mode}
          type="button"
          onClick={() => value !== mode && onChange(mode)}
          aria-pressed={value === mode}
          className={cn(
            "inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-medium transition-colors",
            value === mode
              ? "bg-secondary text-foreground"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          <Icon className="h-3.5 w-3.5" />
          {label}
        </button>
      ))}
    </div>
  );
}

function GroupByToggles({
  groupByPuller,
  groupByPurchase,
  isBuybackEvent,
  onTogglePuller,
  onTogglePurchase,
}: {
  groupByPuller: boolean;
  groupByPurchase: boolean;
  isBuybackEvent: boolean;
  onTogglePuller: () => void;
  onTogglePurchase: () => void;
}) {
  return (
    <div className="inline-flex items-center gap-1">
      <button
        type="button"
        onClick={onTogglePuller}
        aria-pressed={groupByPuller}
        className={cn(
          "inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-medium transition-colors border",
          groupByPuller
            ? "bg-secondary text-foreground border-border"
            : "text-muted-foreground hover:text-foreground border-transparent",
        )}
      >
        <Users className="h-3.5 w-3.5" />
        By Puller
      </button>
      {isBuybackEvent && (
        <button
          type="button"
          onClick={onTogglePurchase}
          aria-pressed={groupByPurchase}
          className={cn(
            "inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-medium transition-colors border",
            groupByPurchase
              ? "bg-secondary text-foreground border-border"
              : "text-muted-foreground hover:text-foreground border-transparent",
          )}
        >
          <Tag className="h-3.5 w-3.5" />
          By Purchase
        </button>
      )}
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: [string, string][];
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={cn(FILTER_CONTROL_CLASS, "w-auto min-w-[110px]")} aria-label={label}>
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent>
        {options.map(([v, l]) => (
          <SelectItem key={v} value={v} className="text-xs">{l}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// Color-coded multi-select for the Status column. The trigger shows "Status"
// (plus a count when items are selected) with small coloured chips for the
// chosen statuses; the popover lists every option as a checkbox row with a dot
// matching its pill colour. An empty selection means "show all".
function StatusMultiSelect({
  selected,
  onChange,
  counts,
}: {
  selected: StatusValue[];
  onChange: (next: StatusValue[]) => void;
  counts: Partial<Record<StatusValue, number>>;
}) {
  const toggle = (value: StatusValue) => {
    onChange(
      selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value],
    );
  };
  const count = selected.length;
  const allSelected = count === STATUS_OPTIONS.length;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          aria-label="Status"
          className={cn(FILTER_CONTROL_CLASS, "flex items-center gap-1.5")}
        >
          <span>Status{count > 0 ? ` · ${count}` : ""}</span>
          {count > 0 && (
            <span className="flex items-center gap-0.5">
              {selected.slice(0, 4).map((v) => {
                const opt = STATUS_OPTIONS.find((o) => o.value === v);
                return (
                  <span
                    key={v}
                    className={cn("h-2 w-2 rounded-full", opt?.dot ?? "bg-muted-foreground")}
                  />
                );
              })}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-1">
        <div className="flex items-center justify-between px-2 py-1">
          <span className="text-[11px] font-medium text-muted-foreground">Status</span>
          <div className="flex items-center gap-2">
            <button
              className="text-[11px] text-muted-foreground hover:text-foreground"
              onClick={() =>
                onChange(allSelected ? [] : STATUS_OPTIONS.map((o) => o.value))
              }
            >
              {allSelected ? "Deselect All" : "Select All"}
            </button>
            {count > 0 && (
              <button
                className="text-[11px] text-muted-foreground hover:text-foreground"
                onClick={() => onChange([])}
              >
                Clear
              </button>
            )}
          </div>
        </div>
        <div className="max-h-72 overflow-y-auto">
          {STATUS_OPTIONS.map((opt) => {
            const checked = selected.includes(opt.value);
            return (
              <button
                key={opt.value}
                onClick={() => toggle(opt.value)}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded-sm text-xs hover:bg-accent text-left"
              >
                <Checkbox checked={checked} className="h-3.5 w-3.5 pointer-events-none" />
                <span className={cn("h-2.5 w-2.5 rounded-full shrink-0", opt.dot)} />
                <span className="text-foreground">{opt.label}</span>
                <span className="ml-auto text-[10px] tabular-nums text-muted-foreground">
                  {counts[opt.value] ?? 0}
                </span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ToggleChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        FILTER_CONTROL_CLASS,
        active && "bg-primary/15 text-primary border-primary/40 hover:text-primary",
      )}
    >
      {children}
    </button>
  );
}

// Parse an event's date string ("YYYY-MM-DD ...") into a local-midnight Date.
// Mirrors the same helper in events-sidebar.tsx so the home auto-select uses an
// identical past/upcoming threshold. Returns null when there's no usable date.
function parseEventDate(s?: string | null): Date | null {
  if (!s) return null;
  const m = s.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

// Re-exported for potential reuse; keeps holdState tree-shakeable.
export { holdState };
